/**
 * ADR-0233 — the weights editor for the Experiments view.
 *
 * Three number inputs + sliders in lock-step. The parent owns the
 * draft weights; this component just renders + emits onChange. Save
 * is enabled only when the draft differs from the current config and
 * passes `validateWeights` (sum to 100, integers in [0, 100]).
 */

import { useId } from 'react';

import type { HomeExperimentWeights, HomeVariant } from '@sill/types/api';

import { useC } from '../../lib/theme.js';
import { VARIANTS, VARIANT_LABEL, validateWeights } from './experiments-helpers.js';
import { Tooltip } from './Tooltip.js';

const TIP_SPLIT =
  'Share of new visitors sent to each variant. Existing visitors keep their variant.';
const TIP_SAVE =
  'Save weights. Existing visitors keep their variant; changes reach new visitors within 60 seconds.';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface WeightsEditorProps {
  value: HomeExperimentWeights;
  disabled?: boolean;
  onChange: (next: HomeExperimentWeights) => void;
  onSave: () => void;
  dirty: boolean;
  pending: boolean;
}

export function WeightsEditor({
  value,
  disabled = false,
  onChange,
  onSave,
  dirty,
  pending,
}: WeightsEditorProps): JSX.Element {
  const C = useC();
  const idPrefix = useId();
  const sum = value.a + value.b + value.c;
  const validationError = validateWeights(value);

  const setOne = (v: HomeVariant, next: number): void => {
    const clamped = Math.max(0, Math.min(100, Math.round(Number.isFinite(next) ? next : 0)));
    onChange({ ...value, [v]: clamped });
  };

  return (
    <div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 1.5,
          color: C.muted,
          textTransform: 'uppercase',
          marginBottom: 8,
          display: 'inline-flex',
          alignItems: 'center',
        }}
      >
        Traffic split
        <Tooltip label={TIP_SPLIT} testId="tt-weights-split" />
      </div>
      <div style={{ display: 'grid', gap: 10 }}>
        {VARIANTS.map((v) => {
          const inputId = `${idPrefix}-${v}`;
          return (
            <div
              key={v}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(140px, 1fr) minmax(0, 2fr) 76px',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <label
                htmlFor={inputId}
                style={{
                  fontFamily: sansFont,
                  fontSize: 13,
                  color: C.body,
                }}
              >
                {VARIANT_LABEL[v]}
              </label>
              <input
                id={`${inputId}-range`}
                type="range"
                min={0}
                max={100}
                step={1}
                value={value[v]}
                disabled={disabled || pending}
                onChange={(e) => setOne(v, Number(e.target.value))}
                aria-label={`${VARIANT_LABEL[v]} weight`}
                style={{ width: '100%' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  id={inputId}
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={value[v]}
                  disabled={disabled || pending}
                  onChange={(e) => setOne(v, Number(e.target.value))}
                  aria-label={`${VARIANT_LABEL[v]} weight (percent)`}
                  style={{
                    width: 56,
                    fontFamily: monoFont,
                    fontSize: 12,
                    padding: '4px 6px',
                    background: C.surface2,
                    color: C.text,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                    textAlign: 'right',
                  }}
                />
                <span
                  style={{
                    fontFamily: monoFont,
                    fontSize: 11,
                    color: C.muted,
                  }}
                >
                  %
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginTop: 12,
          flexWrap: 'wrap',
        }}
      >
        <div
          data-testid="weights-sum"
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: sum === 100 ? C.muted : C.coralFg,
          }}
        >
          Sum: {sum} / 100
        </div>
        {validationError !== null ? (
          <div
            role="alert"
            data-testid="weights-error"
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.coralFg,
            }}
          >
            {validationError}
          </div>
        ) : null}
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <button
            type="button"
            onClick={onSave}
            disabled={disabled || pending || !dirty || validationError !== null}
            data-testid="weights-save"
            style={{
              fontFamily: sansFont,
              fontSize: 12.5,
              fontWeight: 600,
              padding: '7px 14px',
              background: dirty && validationError === null ? C.lime : 'transparent',
              color: dirty && validationError === null ? C.ink : C.muted,
              border: `1px solid ${dirty && validationError === null ? C.lime : C.border}`,
              borderRadius: 3,
              cursor: dirty && validationError === null ? 'pointer' : 'not-allowed',
              opacity: pending ? 0.6 : 1,
            }}
          >
            {pending ? 'Saving…' : 'Save weights'}
          </button>
          <Tooltip label={TIP_SAVE} testId="tt-weights-save" />
        </span>
      </div>
    </div>
  );
}
